/**
 * Document-type tagger. Titles, folder paths, and tags only — never file bytes.
 * Rules live in config/diligence/doc-types.v2.json so the Python adapter stays aligned.
 */
import rulesDoc from '../../config/diligence/doc-types.v2.json' with { type: 'json' };

export const DOC_TYPE_VERSION = rulesDoc.version;
export const DOC_TYPE_RULES = rulesDoc.rules;

export function docBlob(doc) {
  return [doc?.title, doc?.fileName, doc?.folder, doc?.path, doc?.relativePath, ...(doc?.tags || [])]
    .filter((v) => v != null && v !== '')
    .join(' ')
    .toLowerCase();
}

function hasToken(blob, token) {
  const t = String(token || '').toLowerCase().replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  if (!t) return false;
  return new RegExp(`(^|[^a-z0-9])${t}([^a-z0-9]|$)`).test(blob);
}

/**
 * Friendly names for the Data Room Type column.
 * Rule `label` wins. Everything else is a readable name, never the raw id.
 */
const DOC_TYPE_LABELS = {
  jib: 'JIB',
  swd: 'SWD',
  joa: 'JOA',
  copas: 'COPAS',
  afe: 'AFE',
  ami: 'AMI',
  ppr: 'Preferential right',
  sua: 'Surface use agreement',
  row: 'Right-of-way',
  psa: 'Purchase agreement',
  assignment_bos: 'Assignment / bill of sale',
  los_by_well: 'LOS by well',
  rrc_14b2: 'RRC 14B2',
  rrc_p4: 'RRC P-4',
  rrc_p5: 'RRC P-5',
  rrc_p12: 'RRC P-12',
  rrc_p16: 'RRC P-16',
  amendment: 'Lease amendment',
  extension: 'Lease extension',
  well_log: 'Well log',
  wellbore_diagram: 'Wellbore diagram',
};

export function docTypeLabel(id) {
  if (!id) return '';
  const rule = DOC_TYPE_RULES.find((r) => r.docType === id);
  if (rule?.label) return rule.label;
  if (DOC_TYPE_LABELS[id]) return DOC_TYPE_LABELS[id];
  return String(id)
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/**
 * File format for a production-history or seller-forecast document.
 * Tool and spreadsheet names are provenance, not a separate type.
 */
export function sourceFormat(doc) {
  const blob = docBlob(doc);
  if (/phd\s*win|\.phz(?:[^a-z0-9]|$)|\.phd(?:[^a-z0-9]|$)/.test(blob)) return 'PHDWin';
  if (/(?:^|[^a-z0-9])aries(?:[^a-z0-9]|$)|\.mdb(?:[^a-z0-9]|$)|\.accdb(?:[^a-z0-9]|$)/.test(blob)) return 'ARIES';
  if (/\.(?:xlsx|xls|csv)(?:[^a-z0-9]|$)/.test(blob)) return 'spreadsheet';
  return null;
}

/** @returns {string|null} docType */
export function classifyDoc(doc, rules = DOC_TYPE_RULES) {
  const blob = docBlob(doc);
  if (!blob.trim()) return null;
  for (const rule of rules) {
    if ((rule.phrases || []).some((p) => blob.includes(String(p).toLowerCase()))) return rule.docType;
    if ((rule.tokens || []).some((t) => hasToken(blob, t))) return rule.docType;
    if ((rule.patterns || []).some((p) => p && new RegExp(p, 'i').test(blob))) return rule.docType;
  }
  return null;
}

/**
 * Document type for the Data Room and checklist.
 * Stored docType wins. Otherwise classify from the file name and folder path
 * only — never file bytes, and not well-level tags.
 */
export function resolvedDocType(doc) {
  if (doc?.docType) return doc.docType;
  return classifyDoc({
    title: doc?.title,
    fileName: doc?.fileName,
    folder: doc?.folder,
    path: doc?.path,
    relativePath: doc?.relativePath,
  });
}
